import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ChevronRight, ChevronDown, Plus, MapPin, Calendar, Users, Wallet, CheckCircle2, Circle, Clock, Edit2, X, Sparkles, Trash2, Link2, RefreshCw, Loader2, Package, FileText } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { apiGet, apiPost, apiPut, apiDel } from '../lib/api';
import { inrCompact, formatDate, formatMonth, statusColor } from '../lib/format';
import { toast } from 'sonner';
import BOQSchedule from './BOQSchedule';
import LabourPanel from '../components/LabourPanel';
import KanbanBoard from '../components/KanbanBoard';
import BillingPaymentTermsFields, { normalizePaymentTerms, DEFAULT_PAYMENT_TERMS } from '../components/BillingPaymentTermsFields';
import { useAuth } from '../contexts/AuthContext';
import { WBSBOQLinkModal, WBSBOQCoverage } from '../components/WBSBOQLink';
import WorkOrdersTab from './projects/WorkOrdersTab';
import ProjectSiteDiary from './projects/ProjectSiteDiary';
import AIInsightsTab from './projects/AIInsightsTab';
import { PrimaveraTab } from './projects/PrimaveraTab';
import EmployeePicker from '../components/EmployeePicker';
import {
  MPRTab, LabourTab, MaterialReceivedTab, WDQTab, MachineryTab,
  HindranceTab, WeatherTab, SafetyTab, RFITab,
} from './projects/DashboardTabs';
import CommittedCostCard from './procurement/CommittedCostCard';
import { useUrlTab } from '../lib/hooks/useUrlState';
import { ProjectGanttPanel } from './projects/ProjectGantt';
import PendingDprCard from '../components/dpr/PendingDprCard';

// BUILD 1: Project Team tab component (inline — small enough)
function ProjectTeamTab({ projectId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    apiGet(`/projects/${projectId}/access-list`).then(setData).catch(() => {});
  }, [projectId]);
  if (!data) return <div className="py-8 text-center text-sm text-slate-400">Loading team data…</div>;
  return (
    <div className="space-y-6" data-testid="project-team-tab">
      {/* PM MPR Access — simple rule */}
      <div className="p-3 bg-slate-50 border border-slate-200 rounded text-xs text-slate-600 mb-3" data-testid="pm-mpr-guide">
        The employee selected as <strong>Project Manager</strong> in the project's Edit form automatically has MPR approval access for this project.
      </div>
      <div>
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Project Team ({data.team?.length || 0})</h3>
        {(data.team || []).length === 0
          ? <p className="text-sm text-slate-400 italic">No team members added yet.</p>
          : <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
              {data.team.map((m, i) => (
                <div key={i} className="border border-slate-200 rounded-sm px-3 py-2 bg-white">
                  <div className="font-display font-semibold text-sm">{m.name}</div>
                  <div className="text-xs text-slate-500">{m.role} {m.designation ? '· ' + m.designation : ''}</div>
                  {m.emp_id && <div className="text-[10px] text-slate-400 font-mono mt-0.5">{m.emp_id}</div>}
                </div>
              ))}
            </div>
        }
      </div>
      <div>
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Assigned Employees ({data.assigned_employees?.length || 0})</h3>
        {(data.assigned_employees || []).length === 0
          ? <p className="text-sm text-slate-400 italic">No employees directly assigned to this project.</p>
          : <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
              {data.assigned_employees.map(e => (
                <div key={e.id} className="border border-slate-200 rounded-sm px-3 py-2 bg-white">
                  <div className="font-display font-semibold text-sm">{e.name}</div>
                  <div className="text-xs text-slate-500">{e.designation} {e.department ? '· ' + e.department : ''}</div>
                  <div className="text-[10px] text-slate-400 font-mono mt-0.5">{e.emp_id}</div>
                </div>
              ))}
            </div>
        }
      </div>
    </div>
  );
}

const TABS = [
  'Overview', 'Site Diary', 'Work Orders', 'Stages', 'WBS', 'Gantt', 'BOQ & Schedule',
  'Labour', 'Tasks', 'Risks',
  // Per-project Dashboard sub-tabs (Iter 124 — P0):
  'MPR', 'Staff/Labour', 'Material Received', 'WDQ', 'Machinery',
  'Hindrance', 'Weather', 'Safety', 'RFI',
  // Material Requisitions raised by the Projects team — feeds Procurement.
  'Material Requisitions',
  // Phase 8 — Advanced AI Intelligence Layer.
  'AI Insights',
  // Phase C — Primavera P6 integration.
  'Primavera',
  // BUILD 1 — Project team + access list.
  'Team',
];
const RISK_COLORS = {
  Low: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  Medium: 'bg-amber-50 text-amber-700 border border-amber-200',
  High: 'bg-red-50 text-red-700 border border-red-200',
};
const SCORE_COLORS = {
  1: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  2: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  3: 'bg-amber-50 text-amber-700 border border-amber-200',
  4: 'bg-amber-50 text-amber-700 border border-amber-200',
  6: 'bg-red-50 text-red-700 border border-red-200',
  9: 'bg-red-50 text-red-700 border border-red-200',
};

export default function ProjectDetail() {
  const { user } = useAuth();
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [wbs, setWbs] = useState([]);
  const [risks, setRisks] = useState([]);
  const [urlTab, setTab] = useUrlTab('tab', 'Overview');
  const tab = TABS.includes(urlTab) ? urlTab : 'Overview';
  const [editing, setEditing] = useState(false);
  const isMD = user?.role === 'MD' || user?.role === 'super_admin';

  const load = async () => {
    try {
      const p = await apiGet(`/projects/${id}`);
      setProject(p);
      const w = await apiGet('/wbs');
      setWbs(w.filter(x => x.project_id === id));
      const r = await apiGet('/risks');
      setRisks(r.filter(x => x.project_id === id));
    } catch (e) { /* noop */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!project) return <div className="p-8 text-sm text-slate-500 font-mono">Loading…</div>;

  return (
    <div>
      <Link to="/app/projects" data-testid="back-to-projects" className="inline-flex items-center gap-1 text-xs font-display font-bold uppercase tracking-widest text-slate-500 hover:text-[#3b4872] mb-4">
        <ArrowLeft size={13} /> All Projects
      </Link>
      <PageHeader
        overline={project.code}
        title={project.name}
        subtitle={[project.client, project.type, project.location].filter(Boolean).join(' · ') || '—'}
        actions={
          <div className="flex items-center gap-2">
            <span className={`tag ${statusColor(project.status)}`}>{project.status}</span>
            {isMD && (
              <button onClick={() => setEditing(true)} data-testid="project-edit-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-white border border-slate-300 hover:border-[#3b4872] hover:text-[#3b4872] rounded-sm font-display font-semibold">
                <Edit2 size={12} /> Edit Project
              </button>
            )}
          </div>
        }
      />

      {editing && <ProjectEditModal project={project} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} />}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <Stat label="Contract Value" value={inrCompact(project.contract_value)} icon={Wallet} />
        <Stat label="Spent" value={inrCompact(project.spent)} sub={Math.round((project.spent || 0) / (project.contract_value || 1) * 100) + '% of value'} />
        <Stat label="Progress" value={project.progress == null ? '—' : project.progress + '%'} sub={project.progress_basis ? `Basis: ${project.progress_basis.replace('_', ' ')}` : undefined} bar={project.progress || 0} />
        <Stat label="Workforce" value={(project.workforce || 0).toLocaleString('en-IN')} icon={Users} />
        <Stat label="Risk" value={project.risk_level} isRisk />
      </div>

      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="border-b border-slate-200 flex overflow-x-auto sticky top-0 z-20 bg-white" data-testid="project-tabs">
          {TABS.map(t => (
            <button
              key={t}
              data-testid={'tab-' + t.toLowerCase()}
              onClick={() => setTab(t)}
              className={'relative px-4 py-3 text-sm font-display font-semibold transition-colors whitespace-nowrap ' + (tab === t ? 'text-[#0F172A]' : 'text-slate-500 hover:text-[#0F172A]')}
            >
              {t}
              {tab === t && <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-[#3b4872]" />}
            </button>
          ))}
        </div>
        <div className="p-5">
          {tab === 'Overview' && (<><Overview project={project} /><PendingDprCard projectId={project.id} /><CommittedCostCard projectId={project.id} /></>)}
          {tab === 'Site Diary' && <ProjectSiteDiary project={project} />}
          {tab === 'Work Orders' && <WorkOrdersTab project={project} reload={load} />}
          {tab === 'Stages' && <StagesTimeline project={project} reload={load} />}
          {tab === 'WBS' && <WBSTree project={project} wbs={wbs} reload={load} />}
          {tab === 'Gantt' && <GanttTabPanel project={project} wbs={wbs} />}
          {tab === 'BOQ & Schedule' && <BOQSchedule project={project} />}
          {tab === 'Labour' && <ProjectLabour project={project} />}
          {tab === 'Tasks' && <ProjectTasks project={project} />}
          {tab === 'Risks' && <RiskMatrix risks={risks} project={project} reload={load} />}
          {tab === 'MPR' && <MPRTab project={project} />}
          {tab === 'Staff/Labour' && <LabourTab project={project} />}
          {tab === 'Material Received' && <MaterialReceivedTab project={project} />}
          {tab === 'WDQ' && <WDQTab project={project} />}
          {tab === 'Machinery' && <MachineryTab project={project} />}
          {tab === 'Hindrance' && <HindranceTab project={project} />}
          {tab === 'Weather' && <WeatherTab project={project} />}
          {tab === 'Safety' && <SafetyTab project={project} />}
          {tab === 'RFI' && <RFITab project={project} />}
          {tab === 'Material Requisitions' && <MaterialRequisitionsTab project={project} user={user} />}
          {tab === 'AI Insights' && <AIInsightsTab project={project} />}
          {tab === 'Primavera' && <PrimaveraTab project={project} />}
          {tab === 'Team' && <ProjectTeamTab projectId={project.id} />}
        </div>
      </div>

    </div>
  );
}

function Stat({ label, value, sub, icon: Icon, bar, isRisk }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="kpi-overline">{label}</div>
        {Icon && <Icon size={13} className="text-slate-400" />}
      </div>
      {isRisk
        ? <span className={'tag ' + (RISK_COLORS[value] || 'bg-slate-100 text-slate-700')}>{value}</span>
        : <div className="font-display text-xl font-extrabold tracking-tight">{value}</div>
      }
      {bar !== undefined && (
        <div className="h-1 bg-slate-100 mt-2 rounded-sm overflow-hidden">
          <div className="h-full bg-[#3b4872]" style={{ width: bar + '%' }} />
        </div>
      )}
      {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}


function ProjectEditModal({ project, onClose, onSaved }) {
  const [form, setForm] = useState({
    code: project.code || '',
    name: project.name || '',
    client: project.client || '',
    type: project.type || '',
    contract_type: project.contract_type || 'Lumpsum Contract',
    location: project.location || '',
    pm: project.pm || '',
    pm_employee_uuid: project.pm_employee_uuid || null,
    pm_email: project.pm_email || null,
    pm_emp_id: project.pm_emp_id || null,
    start_date: project.start_date || '',
    end_date: project.end_date || '',
    contract_value: project.contract_value || 0,
    status: project.status || 'Active',
    risk_level: project.risk_level || 'Medium',
    workforce: project.workforce || 0,
    billing_type: project.billing_type || 'Milestone Basis',
    ra_bill_frequency: project.ra_bill_frequency || 'Monthly',
    payment_terms: { ...DEFAULT_PAYMENT_TERMS, ...(project.payment_terms || {}) },
  });
  const [templates, setTemplates] = useState({});
  const [billingTypes, setBillingTypes] = useState(['Milestone Basis', 'RA Bill Basis']);
  const [raFrequencies, setRaFrequencies] = useState(['Weekly', 'Fortnightly', 'Monthly']);
  const [defaultPT, setDefaultPT] = useState(DEFAULT_PAYMENT_TERMS);
  const [busy, setBusy] = useState(false);
  const [regenStages, setRegenStages] = useState(false);

  useEffect(() => {
    apiGet('/projects/stage-templates').then(t => {
      setTemplates(t.templates || {});
      if (t.billing_types) setBillingTypes(t.billing_types);
      if (t.ra_bill_frequencies) setRaFrequencies(t.ra_bill_frequencies);
      if (t.default_payment_terms) setDefaultPT(t.default_payment_terms);
    }).catch(() => {});
  }, []);

  const contractTypeChanged = form.contract_type !== project.contract_type;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        contract_value: Number(form.contract_value || 0),
        workforce: Number(form.workforce || 0),
        payment_terms: normalizePaymentTerms(form.payment_terms, defaultPT),
      };
      if (payload.billing_type !== 'RA Bill Basis') payload.ra_bill_frequency = null;
      // If contract_type changed AND user opted to regen stages, omit 'stages' so backend rebuilds
      if (contractTypeChanged && regenStages) {
        // backend regenerates because we DON'T send stages
      } else {
        // Preserve existing stages
        payload.stages = project.stages;
      }
      await apiPut(`/projects/${project.id}`, payload);
      toast.success('Project updated');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-white rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872]">MD · Edit Project</div>
            <h3 className="font-display font-bold text-xl mt-1">{project.code}</h3>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>

        <div className="p-5 grid grid-cols-2 gap-3">
          <FieldGrid label="Project Code"><input value={form.code} onChange={e=>setForm({...form,code:e.target.value})} className={inputCls} required /></FieldGrid>
          <FieldGrid label="Status">
            <select value={form.status} onChange={e=>setForm({...form,status:e.target.value})} className={`${inputCls} bg-white`}>
              {['Active','Delayed','On Hold','Completed','Cancelled'].map(s => <option key={s}>{s}</option>)}
            </select>
          </FieldGrid>
          <FieldGrid label="Project Name" full><input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} className={inputCls} required /></FieldGrid>
          <FieldGrid label="Client"><input value={form.client} onChange={e=>setForm({...form,client:e.target.value})} className={inputCls} /></FieldGrid>
          <FieldGrid label="Type"><input value={form.type} onChange={e=>setForm({...form,type:e.target.value})} className={inputCls} placeholder="Civil / EPC / Infrastructure" /></FieldGrid>
          <FieldGrid label="Project Manager">
            <EmployeePicker
              value={form.pm || ''}
              onChange={(name, empId, empObj) => setForm({
                ...form,
                pm: name,
                pm_employee_uuid: empId || null,
                pm_email: empObj?.email || null,
                pm_emp_id: empObj?.emp_id || null,
              })}
              placeholder="Search employee by name or ID…"
              allowFreeText
              testid="project-pm-picker"
            />
            {form.pm_email && (
              <div className="text-[11px] text-emerald-700 mt-1 font-medium" data-testid="pm-linked-badge">
                Linked: {form.pm_email}
              </div>
            )}
            {form.pm && !form.pm_email && (
              <div className="text-[11px] text-amber-600 mt-1">
                Not linked — search from the employee list above for PM access to take effect.
              </div>
            )}
          </FieldGrid>
          <FieldGrid label="Location"><input value={form.location} onChange={e=>setForm({...form,location:e.target.value})} className={inputCls} /></FieldGrid>
          <FieldGrid label="Start Date"><input type="date" value={form.start_date?.slice(0,10) || ''} onChange={e=>setForm({...form,start_date:e.target.value})} className={inputCls} /></FieldGrid>
          <FieldGrid label="End Date"><input type="date" value={form.end_date?.slice(0,10) || ''} onChange={e=>setForm({...form,end_date:e.target.value})} className={inputCls} /></FieldGrid>
          <FieldGrid label="Contract Value (₹)"><input type="number" value={form.contract_value} onChange={e=>setForm({...form,contract_value:e.target.value})} className={`${inputCls} tabular-nums`} /></FieldGrid>
          <FieldGrid label="Risk Level">
            <select value={form.risk_level} onChange={e=>setForm({...form,risk_level:e.target.value})} className={`${inputCls} bg-white`}>
              {['Low','Medium','High'].map(r => <option key={r}>{r}</option>)}
            </select>
          </FieldGrid>

          <FieldGrid label="Contract Type" full>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {Object.keys(templates).map(ct => (
                <button key={ct} type="button" data-testid={`edit-contract-type-${ct.replace(/[^a-z0-9]+/gi,'-').toLowerCase()}`}
                  onClick={() => setForm({...form, contract_type: ct})}
                  className={`px-3 py-2 text-xs font-display font-semibold border rounded-sm transition-all ${form.contract_type === ct ? 'border-[#3b4872] bg-indigo-50 text-[#3b4872]' : 'border-slate-300 hover:border-slate-400 text-slate-600'}`}>
                  {ct}
                </button>
              ))}
            </div>
          </FieldGrid>

          {contractTypeChanged && (
            <div className="col-span-2 bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-900">
              <div className="font-display font-bold uppercase tracking-widest text-[10px] mb-1.5">Contract Type Changed</div>
              <p className="mb-2">You changed contract type from <b>{project.contract_type}</b> to <b>{form.contract_type}</b>. Stages currently in progress may not match the new template.</p>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={regenStages} onChange={e=>setRegenStages(e.target.checked)} data-testid="edit-regen-stages" />
                <span><b>Regenerate stages</b> from new contract template (current stage progress will be lost)</span>
              </label>
            </div>
          )}

          <BillingPaymentTermsFields
            billingType={form.billing_type}
            raBillFrequency={form.ra_bill_frequency}
            paymentTerms={form.payment_terms}
            billingTypes={billingTypes}
            raFrequencies={raFrequencies}
            testIdPrefix="edit"
            onChange={({ billingType, raBillFrequency, paymentTerms }) =>
              setForm({
                ...form,
                billing_type: billingType,
                ra_bill_frequency: raBillFrequency,
                payment_terms: paymentTerms,
              })
            }
          />
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2 bg-slate-50 sticky bottom-0">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-100 font-display">Cancel</button>
          <button type="submit" disabled={busy} data-testid="project-edit-save"
            className="px-4 py-2 text-sm bg-[#3b4872] text-white rounded-sm font-display font-semibold disabled:opacity-50">
            {busy ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
}

const inputCls = 'w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-[#3b4872] focus:ring-1 focus:ring-[#3b4872] focus:outline-none';
const FieldGrid = ({ label, children, full }) => (
  <div className={full ? 'col-span-2' : ''}>
    <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">{label}</label>
    {children}
  </div>
);

function Overview({ project }) {
  // Tendering_30Sep2026 §2 — award_enquiry() writes commencement_date/completion_date/
  // payment_advance_pct/payment_retention_pct/defect_liability_period/payment_terms(string)/
  // contract{loa_date,execution_notes} directly on the project — different field names to
  // the manual-edit flow's start_date/end_date/payment_terms{object}. Fall back to both.
  const [contractRec, setContractRec] = useState(null);
  useEffect(() => {
    if (!project?.id) { setContractRec(null); return; }
    apiGet(`/project-contracts/${project.id}`).then(setContractRec).catch(() => setContractRec(null));
  }, [project?.id]);

  const billingLabel = project.billing_type
    ? (project.billing_type === 'RA Bill Basis'
        ? `RA Bill (${project.ra_bill_frequency || 'Monthly'})`
        : 'Milestone Basis')
    : '—';
  const awardContract = project.contract || {};
  const startDate = project.start_date || project.commencement_date || contractRec?.start_date;
  const endDate = project.end_date || project.completion_date || contractRec?.completion_date;
  const loaDate = awardContract.loa_date || project.loa_date || contractRec?.award_date;
  const loaRef = awardContract.loa_ref || contractRec?.loa_ref;
  const fields = [
    ['Project Manager', project.pm, null],
    ['Type', project.type, null],
    ['Contract Type', project.contract_type || '—', null],
    ['Client Billing', billingLabel, null],
    ['Location', project.location, MapPin],
    ['Start Date', formatDate(startDate), Calendar],
    ['Planned End Date', formatDate(endDate), Calendar],
    ['LOA Date', formatDate(loaDate), FileText],
    ['LOA Reference', loaRef, FileText],
    ['Contract Value', inrCompact(project.contract_value), null],
    ['BOQ Total (live)', project.contract_value_boq ? inrCompact(project.contract_value_boq) : '—', null],
    ['Spent to Date', inrCompact(project.spent), null],
    ['Risk Level', project.risk_level, null],
  ];
  const pt = (project.payment_terms && typeof project.payment_terms === 'object') ? project.payment_terms : {};
  const paymentTermsText = (typeof project.payment_terms === 'string' && project.payment_terms) || contractRec?.payment_terms;
  const executionNotes = awardContract.execution_notes || contractRec?.notes;
  const ptCards = [
    ['Mobilisation Advance', pt.mobilization_advance_pct ?? project.payment_advance_pct ?? contractRec?.mobilisation_advance_pct, '% of contract'],
    ['Material Advance',     pt.material_advance_pct,     '% of contract'],
    ['Advance BG',           pt.advance_bg_pct,           '% of advance'],
    ['Performance BG',       pt.performance_bg_pct,       '% of contract'],
    ['Retention',            pt.retention_pct ?? project.payment_retention_pct ?? contractRec?.retention_pct, '% of bill'],
    ['DLP',                  pt.dlp_months ?? contractRec?.dlp_months ?? project.defect_liability_period, pt.dlp_months || contractRec?.dlp_months ? 'months' : ''],
    ['Credit Days',          contractRec?.credit_days ?? project.payment_credit_days, 'days'],
    ['RA Cert. Period',      pt.ra_certification_days,    'days'],
    ['Bill Processing',      pt.bill_processing_days,     'days'],
  ];
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {fields.map(([l, v, Icon]) => (
          <div key={l} className="border-b border-slate-100 pb-3">
            <div className="kpi-overline mb-1.5">{l}</div>
            <div className="flex items-center gap-2 font-display font-semibold text-[15px]">
              {Icon && <Icon size={14} className="text-slate-400" />}
              {v || '—'}
            </div>
          </div>
        ))}
      </div>
      {(paymentTermsText || project.payment_terms || project.billing_type) && (
        <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="overview-payment-terms">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="kpi-overline mb-1">Payment Terms</div>
              <div className="font-display font-bold text-base">Contractual Cashflow Levers</div>
            </div>
          </div>
          {paymentTermsText && (
            <div className="mb-3 text-sm text-slate-700" data-testid="overview-payment-terms-text">{paymentTermsText}</div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {ptCards.map(([l, v, suffix]) => (
              <div key={l} className="bg-slate-50 border border-slate-200 rounded-sm p-3">
                <div className="kpi-overline text-[9px] mb-1">{l}</div>
                <div className="font-display font-extrabold text-lg tabular-nums">
                  {v ?? '—'}
                  <span className="text-[10px] text-slate-400 font-mono ml-1">{suffix}</span>
                </div>
              </div>
            ))}
          </div>
          {pt.ld_clause && (
            <div className="mt-3 text-xs text-slate-600">
              <span className="kpi-overline mr-2">LD Clause</span>
              <span className="font-mono">{pt.ld_clause}</span>
            </div>
          )}
          {executionNotes && (
            <div className="mt-3 text-xs text-slate-600" data-testid="overview-execution-notes">
              <span className="kpi-overline mr-2">Execution Notes</span>
              <span>{executionNotes}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ----- Stages Timeline -----
function StagesTimeline({ project, reload }) {
  const stages = project.stages || [];
  const [busy, setBusy] = useState(false);

  const advance = async () => {
    setBusy(true);
    try {
      await apiPost(`/projects/${project.id}/advance-stage`, {});
      toast.success('Stage advanced');
      await reload();
    } catch { toast.error('Failed to advance'); }
    setBusy(false);
  };

  const regress = async () => {
    if (!window.confirm('Roll back to the previous stage?')) return;
    setBusy(true);
    try {
      await apiPost(`/projects/${project.id}/regress-stage`, {});
      toast.success('Rolled back to previous stage');
      await reload();
    } catch (e) { toast.error('Cannot regress further'); }
    setBusy(false);
  };

  const setStatus = async (key, status) => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/projects/${project.id}/stage/${key}`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, set_current: status === 'Active' }),
      }).then(r => r.json());
      toast.success('Stage updated');
      reload();
    } catch { toast.error('Failed'); }
  };

  if (!stages.length) {
    return <div className="text-center py-12 text-slate-400 text-sm">No stages defined for this project.</div>;
  }

  const completedCount = stages.filter(s => s.status === 'Completed').length;
  const overall = Math.round((completedCount / stages.length) * 100);

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="kpi-overline">Project Stages · {project.contract_type || 'Lumpsum'}</div>
          <h3 className="font-display font-bold text-base mt-1">{stages.length} stages · {completedCount} completed · {overall}% overall</h3>
        </div>
        <button data-testid="advance-stage-btn" onClick={advance} disabled={busy}
          className="px-4 py-2 text-xs bg-[#3b4872] text-white rounded-sm font-display font-semibold hover:bg-[#2c3658] disabled:opacity-50">
          {busy ? 'Working…' : 'Advance to Next Stage'}
        </button>
        <button data-testid="regress-stage-btn" onClick={regress} disabled={busy}
          className="ml-2 px-3 py-2 text-xs bg-white border border-slate-300 text-slate-600 rounded-sm font-display font-semibold hover:border-slate-500 hover:text-slate-900 disabled:opacity-50">
          ↶ Rollback
        </button>
      </div>

      <div className="relative">
        <div className="absolute left-[14px] top-2 bottom-2 w-0.5 bg-slate-200" />
        <div className="space-y-3">
          {stages.map((s, i) => {
            const isCompleted = s.status === 'Completed';
            const isActive = s.status === 'Active';
            const Icon = isCompleted ? CheckCircle2 : isActive ? Clock : Circle;
            const iconCls = isCompleted ? 'text-emerald-600' : isActive ? 'text-[#3b4872]' : 'text-slate-300';
            return (
              <div key={s.key} data-testid={`stage-row-${s.key}`} className="relative pl-10">
                <div className="absolute left-0 top-1.5 bg-white">
                  <Icon size={28} className={iconCls} strokeWidth={isCompleted || isActive ? 2 : 1.5} />
                </div>
                <div className={`bg-white border rounded-sm p-3.5 ${isActive ? 'border-[#3b4872]/60 shadow-[0_0_0_3px_rgba(255,87,34,0.08)]' : 'border-slate-200'}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Stage {i+1}</span>
                        <span className={`tag ${isCompleted ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : isActive ? 'bg-indigo-50 text-[#3b4872] border border-indigo-200' : 'bg-slate-50 text-slate-500 border border-slate-200'}`}>
                          {s.status}
                        </span>
                      </div>
                      <div className="font-display font-bold text-base mt-1">{s.name}</div>
                      {(s.started_at || s.completed_at) && (
                        <div className="text-[11px] text-slate-500 mt-1">
                          {s.started_at && <span>Started · {formatDate(s.started_at)}</span>}
                          {s.completed_at && <span className="ml-3">Completed · {formatDate(s.completed_at)}</span>}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-1.5">
                      {!isCompleted && <button onClick={() => setStatus(s.key, 'Completed')} className="text-[10px] uppercase tracking-widest font-display font-bold text-emerald-700 hover:text-emerald-900">Mark Done</button>}
                      {!isActive && !isCompleted && <button onClick={() => setStatus(s.key, 'Active')} className="text-[10px] uppercase tracking-widest font-display font-bold text-[#3b4872] hover:text-[#2c3658]">Make Active</button>}
                    </div>
                  </div>
                  {(s.progress > 0 && s.progress < 100) && (
                    <div className="mt-2 h-1 bg-slate-100 rounded-sm overflow-hidden">
                      <div className="h-full bg-[#3b4872]" style={{ width: `${s.progress}%` }} />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}


// ----- Project-scoped Labour panel -----
function ProjectLabour({ project }) {
  return <LabourPanel projectId={project.id} />;
}


// ----- Project-scoped Tasks board -----
function ProjectTasks({ project }) {
  return <KanbanBoard projectId={project.id} />;
}


// ----- WBS Tree (flatten-first to avoid recursive JSX) -----
function WBSTree({ project, wbs, reload }) {
  const [expanded, setExpanded] = useState(() => new Set(wbs.filter(w => !w.parent_id).map(w => w.id)));
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ status: 'Not Started', progress: 0 });
  const [linkingWbs, setLinkingWbs] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const flatList = useMemo(() => {
    const map = {};
    wbs.forEach(w => { map[w.id] = { ...w, children: [] }; });
    const roots = [];
    wbs.forEach(w => {
      if (w.parent_id && map[w.parent_id]) map[w.parent_id].children.push(map[w.id]);
      else if (!w.parent_id) roots.push(map[w.id]);
    });
    roots.sort((a, b) => (a.code || '').localeCompare(b.code || ''));
    const out = [];
    const walk = (n, depth) => {
      out.push({ ...n, depth });
      if (expanded.has(n.id)) {
        n.children.sort((a, b) => (a.code || '').localeCompare(b.code || '')).forEach(c => walk(c, depth + 1));
      }
    };
    roots.forEach(r => walk(r, 0));
    return out;
  }, [wbs, expanded]);

  const toggle = (id) => {
    setExpanded(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await apiPost('/wbs', {
        ...form,
        project_id: project.id,
        progress: Number(form.progress || 0),
        weightage: Number(form.weightage || 0),
      });
      toast.success('Activity added');
      setAdding(false);
      setForm({ status: 'Not Started', progress: 0 });
      reload();
    } catch (e) { toast.error('Failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this activity?\n\nThis action cannot be undone.')) return;
    await apiDel('/wbs/' + id);
    toast.success('Deleted');
    reload();
  };

  const triggerRollupAll = async () => {
    setSyncing(true);
    try {
      const res = await apiPost(`/wbs/project/${project.id}/rollup-all`, {});
      toast.success(`Rolled up ${res.boq_items_updated} BOQ items from WBS`);
      reload();
    } catch (e) {
      toast.error('Rollup failed');
    }
    setSyncing(false);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="kpi-overline">Work Breakdown Structure</div>
          <h3 className="font-display font-bold text-base mt-1">
            {wbs.length} activities
            {wbs.some(w => w.is_boq_linked) && (
              <span className="ml-2 text-[10px] uppercase tracking-widest text-[#3b4872] bg-indigo-50 px-1.5 py-0.5 rounded-sm">
                <Sparkles size={9} className="inline mr-0.5" /> BOQ-linked
              </span>
            )}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={triggerRollupAll}
            disabled={syncing}
            data-testid="wbs-rollup-boq-btn"
            className="flex items-center gap-1 px-3 py-1.5 text-xs bg-white border border-slate-300 hover:border-emerald-500 hover:text-emerald-700 rounded-sm font-display font-semibold disabled:opacity-50"
          >
            <RefreshCw size={11} className={syncing ? 'animate-spin' : ''} /> Rollup → BOQ
          </button>
          <button data-testid="wbs-add-btn" onClick={() => setAdding(true)} className="flex items-center gap-2 px-3 py-1.5 text-xs bg-[#3b4872] text-white hover:bg-[#2d3856] rounded-sm hover:bg-[#3b4872] font-display font-semibold">
            <Plus size={13} /> Add Activity
          </button>
        </div>
      </div>

      {/* BOQ Coverage Panel */}
      {wbs.length > 0 && (
        <div className="mb-4">
          <WBSBOQCoverage projectId={project.id} />
        </div>
      )}

      {wbs.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-slate-200 rounded-sm">
          <div className="text-xs uppercase tracking-widest font-display text-slate-400">No activities yet</div>
          <p className="text-sm text-slate-500 my-2">Build your WBS to reflect how you plan to execute the project. Add activities manually, or generate a schedule in Project Planning.</p>
          <div className="flex items-center justify-center gap-2 mt-3">
            <button onClick={() => setAdding(true)} className="text-[#3b4872] text-sm font-display font-semibold" data-testid="wbs-add-manual-empty">+ Add Activity</button>
          </div>
        </div>
      ) : (
        <table className="w-full">
          <thead>
            <tr className="text-left">
              <th className="kpi-overline pb-2 pl-3 border-b border-slate-200">Activity</th>
              <th className="kpi-overline pb-2 pl-3 border-b border-slate-200">Owner</th>
              <th className="kpi-overline pb-2 pl-3 border-b border-slate-200">Schedule</th>
              <th className="kpi-overline pb-2 pl-3 border-b border-slate-200">Progress</th>
              <th className="kpi-overline pb-2 pl-3 border-b border-slate-200">BOQ Links</th>
              <th className="kpi-overline pb-2 pl-3 border-b border-slate-200">Status</th>
              <th className="kpi-overline pb-2 pl-3 border-b border-slate-200"></th>
            </tr>
          </thead>
          <tbody>
            {flatList.map(node => {
              const hasChildren = wbs.some(x => x.parent_id === node.id);
              const isOpen = expanded.has(node.id);
              return (
                <tr key={node.id} className="hover:bg-indigo-50/30">
                  <td style={{ paddingLeft: 12 + node.depth * 24 }} className="py-2.5 border-b border-slate-100">
                    <div className="flex items-center gap-2">
                      {hasChildren ? (
                        <button onClick={() => toggle(node.id)} className="w-5 h-5 flex items-center justify-center text-slate-500 hover:text-[#3b4872]">
                          {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </button>
                      ) : <span className="w-5 inline-block" />}
                      <span className="font-mono text-xs text-slate-500">{node.code}</span>
                      <span className="text-sm font-medium">{node.name}</span>
                      {node.auto_generated && (
                        <span className="tag bg-indigo-50 text-[#3b4872] border border-indigo-200 text-[9px] flex items-center gap-0.5"><Sparkles size={8} /> BOQ</span>
                      )}
                      {node.kind === 'section' && <span className="tag bg-slate-100 text-slate-600 border border-slate-200 text-[9px]">SEC</span>}
                      {node.kind === 'group' && <span className="tag bg-amber-50 text-amber-700 border border-amber-200 text-[9px]">GRP</span>}
                    </div>
                  </td>
                  <td className="py-2.5 border-b border-slate-100 text-xs text-slate-600">{node.owner}</td>
                  <td className="py-2.5 border-b border-slate-100 text-xs">{formatDate(node.start_date)} → {formatDate(node.end_date)}</td>
                  <td className="py-2.5 border-b border-slate-100">
                    <div className="flex items-center gap-2">
                      <div className="w-20 h-1 bg-slate-100 rounded-sm overflow-hidden">
                        <div className="h-full bg-[#3b4872]" style={{ width: (node.progress || 0) + '%' }} />
                      </div>
                      <span className="text-xs font-display font-bold tabular-nums w-9">{(node.progress == null || !node.planned_qty) ? '—' : `${node.progress}%`}</span>
                    </div>
                  </td>
                  <td className="py-2.5 border-b border-slate-100">
                    <button 
                      onClick={() => setLinkingWbs(node)}
                      className="flex items-center gap-1 text-xs hover:text-blue-600"
                      data-testid={`wbs-link-boq-${node.id}`}
                    >
                      <Link2 size={12} />
                      {node.linked_boq_count > 0 ? (
                        <span className="text-blue-600 font-medium">{node.linked_boq_count} items</span>
                      ) : (
                        <span className="text-slate-400">Link BOQ</span>
                      )}
                      {node.budgeted_cost > 0 && (
                        <span className="text-[10px] text-slate-500 ml-1">
                          ₹{(node.budgeted_cost / 100000).toFixed(1)}L
                        </span>
                      )}
                    </button>
                  </td>
                  <td className="py-2.5 border-b border-slate-100">
                    <span className={'tag ' + statusColor(node.status)}>{node.status}</span>
                  </td>
                  <td className="py-2.5 border-b border-slate-100 text-right pr-4">
                    <button onClick={() => remove(node.id)} className="text-xs text-slate-400 hover:text-red-600">Remove</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {adding && <WBSAddModal wbs={wbs} form={form} setForm={setForm} onClose={() => setAdding(false)} onSubmit={submit} />}
      
      {linkingWbs && (
        <WBSBOQLinkModal
          wbsTask={linkingWbs}
          projectId={project.id}
          onClose={() => setLinkingWbs(null)}
          onSaved={reload}
        />
      )}
    </div>
  );
}

function WBSAddModal({ wbs, form, setForm, onClose, onSubmit }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <form onSubmit={onSubmit} onClick={e => e.stopPropagation()} className="bg-white rounded-sm p-5 w-full max-w-xl grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872]">New</div>
          <h3 className="font-display font-bold text-xl mt-1">WBS Activity</h3>
        </div>
        <Input label="Code (e.g. 2.3)" value={form.code} onChange={v => setForm({ ...form, code: v })} />
        <div className="col-span-2"><Input label="Activity Name" value={form.name} onChange={v => setForm({ ...form, name: v })} /></div>
        <Input label="Owner" value={form.owner} onChange={v => setForm({ ...form, owner: v })} />
        <SelectInput label="Parent" value={form.parent_id} onChange={v => setForm({ ...form, parent_id: v })}
          options={[{ v: '', l: '— (top level) —' }, ...wbs.map(w => ({ v: w.id, l: w.code + ' ' + w.name }))]} />
        <Input label="Start" type="date" value={form.start_date} onChange={v => setForm({ ...form, start_date: v })} />
        <Input label="End" type="date" value={form.end_date} onChange={v => setForm({ ...form, end_date: v })} />
        <Input label="Progress %" type="number" value={form.progress} onChange={v => setForm({ ...form, progress: v })} />
        <Input label="Weightage %" type="number" value={form.weightage} onChange={v => setForm({ ...form, weightage: v })} />
        <SelectInput label="Status" value={form.status} onChange={v => setForm({ ...form, status: v })}
          options={[{ v: 'Not Started', l: 'Not Started' }, { v: 'In Progress', l: 'In Progress' }, { v: 'Completed', l: 'Completed' }, { v: 'Delayed', l: 'Delayed' }]} />
        <div className="col-span-2 flex justify-end gap-2 pt-3 border-t border-slate-200">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-50 font-display">Cancel</button>
          <button type="submit" className="px-4 py-2 text-sm bg-[#3b4872] text-white rounded-sm font-display font-semibold">Add Activity</button>
        </div>
      </form>
    </div>
  );
}

function Input({ label, value, onChange, type }) {
  return (
    <div>
      <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">{label}</label>
      <input type={type || 'text'} value={value || ''} onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-[#3b4872] focus:ring-1 focus:ring-[#3b4872] focus:outline-none" />
    </div>
  );
}

function SelectInput({ label, value, onChange, options }) {
  return (
    <div>
      <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">{label}</label>
      <select value={value || ''} onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white focus:border-[#3b4872] focus:ring-1 focus:ring-[#3b4872] focus:outline-none">
        {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </div>
  );
}

// ----- Gantt tab: toggle between the restored WBS-activity Gantt and the
// Plan Baseline (AI plan blocks + CPM) view. Item 9a fix — these are two
// genuinely different data sources (WBS quantity/date rows vs. AI plan
// blocks/micro-tasks) and neither should be dropped in favour of the other. -----
function GanttTabPanel({ project, wbs }) {
  const [view, setView] = useState(null); // null = not yet decided (waiting on plan check)
  const [hasPlanBlocks, setHasPlanBlocks] = useState(null);

  useEffect(() => {
    apiGet(`/project-planning/plans?project_id=${project.id}`)
      .then(rows => setHasPlanBlocks((rows || []).some(p => (p.block_count || 0) > 0)))
      .catch(() => setHasPlanBlocks(false));
  }, [project.id]);

  useEffect(() => {
    if (view || hasPlanBlocks === null) return;
    const hasWbs = wbs.length > 0;
    setView(hasWbs && !hasPlanBlocks ? 'wbs' : 'plan');
  }, [hasPlanBlocks, wbs.length, view]);

  const resolved = view || 'plan';
  return (
    <div>
      <div className="border-b border-slate-200 -mx-5 px-5 flex gap-1 mb-4">
        <button
          onClick={() => setView('wbs')}
          data-testid="gantt-view-wbs-progress"
          className={`flex items-center gap-1.5 px-3 py-2 text-xs font-display font-semibold whitespace-nowrap rounded-t-md ${resolved === 'wbs' ? 'bg-[#3b4872]/10 text-[#3b4872] border-b-2 border-[#3b4872]' : 'text-slate-500 hover:text-[#0F172A] border-b-2 border-transparent'}`}
        >
          WBS Progress
        </button>
        <button
          onClick={() => setView('plan')}
          data-testid="gantt-view-plan-baseline"
          className={`flex items-center gap-1.5 px-3 py-2 text-xs font-display font-semibold whitespace-nowrap rounded-t-md ${resolved === 'plan' ? 'bg-[#3b4872]/10 text-[#3b4872] border-b-2 border-[#3b4872]' : 'text-slate-500 hover:text-[#0F172A] border-b-2 border-transparent'}`}
        >
          Plan Baseline
        </button>
      </div>
      {resolved === 'wbs' ? <Gantt wbs={wbs} /> : <ProjectGanttPanel projectId={project.id} />}
    </div>
  );
}

// ----- Gantt (restored — WBS-activity quantity/date Gantt, Item 9a) -----
function Gantt({ wbs }) {
  if (!wbs.length) return <div className="text-center py-12 text-slate-400 text-sm" data-testid="gantt-wbs-empty">Add WBS activities to see the Gantt chart.</div>;
  const dates = wbs.flatMap(w => [w.start_date, w.end_date]).filter(Boolean).map(d => new Date(d).getTime());
  const min = Math.min(...dates);
  const max = Math.max(...dates);
  const range = max - min || 1;
  const months = [];
  const cur = new Date(min); cur.setDate(1);
  while (cur.getTime() <= max) {
    months.push(new Date(cur));
    cur.setMonth(cur.getMonth() + 1);
  }
  const today = Date.now();
  const flat = wbs.slice().sort((a, b) => (a.code || '').localeCompare(b.code || ''));

  return (
    <div data-testid="gantt-wbs-progress-view">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="kpi-overline">Schedule</div>
          <h3 className="font-display font-bold text-base mt-1">Gantt Chart · Baseline vs Progress</h3>
        </div>
        <div className="flex gap-3 text-xs">
          <span className="flex items-center gap-1.5"><span className="w-3 h-2 bg-[#2F3E46]" /> Baseline</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-2 bg-[#3b4872]" /> Progress</span>
        </div>
      </div>
      <div className="overflow-x-auto border border-slate-200 rounded-sm">
        <div className="min-w-[800px]">
          <div className="flex border-b border-slate-200 bg-slate-50">
            <div className="w-72 shrink-0 px-4 py-2 kpi-overline border-r border-slate-200">Activity</div>
            <div className="flex-1 flex relative">
              {months.map((m, i) => (
                <div key={i} className="flex-1 px-2 py-2 text-[10px] uppercase tracking-widest font-display font-bold text-slate-500 border-r border-slate-100 last:border-r-0">
                  {formatMonth(m)}
                </div>
              ))}
            </div>
          </div>
          {flat.map(w => {
            const s = new Date(w.start_date).getTime();
            const e = new Date(w.end_date).getTime();
            const left = ((s - min) / range) * 100;
            const width = ((e - s) / range) * 100;
            const progressWidth = (width * (w.progress || 0)) / 100;
            return (
              <div key={w.id} className="flex border-b border-slate-100 hover:bg-indigo-50/30">
                <div className="w-72 shrink-0 px-4 py-3 border-r border-slate-200">
                  <div className="font-mono text-[10px] text-slate-500">{w.code}</div>
                  <div className="text-xs font-medium truncate">{w.name}</div>
                </div>
                <div className="flex-1 relative h-12">
                  {today >= min && today <= max && (
                    <div className="absolute top-0 bottom-0 w-px bg-red-400 z-10" style={{ left: ((today - min) / range) * 100 + '%' }}>
                      <div className="absolute top-0 -translate-x-1/2 text-[8px] uppercase tracking-widest font-display font-bold text-red-500">Today</div>
                    </div>
                  )}
                  <div className="absolute top-3 h-3 bg-[#2F3E46] rounded-sm" style={{ left: left + '%', width: width + '%' }} />
                  <div className="absolute top-7 h-2 bg-[#3b4872] rounded-sm" style={{ left: left + '%', width: progressWidth + '%' }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ----- Risk Matrix -----
function RiskMatrix({ risks, project, reload }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ probability: 'M', impact: 'M' });

  const submit = async (e) => {
    e.preventDefault();
    const score = ({ L: 1, M: 2, H: 3 }[form.probability] || 2) * ({ L: 1, M: 2, H: 3 }[form.impact] || 2);
    await apiPost('/risks', { ...form, project_id: project.id, score, status: 'Open' });
    toast.success('Risk added');
    setAdding(false);
    setForm({ probability: 'M', impact: 'M' });
    reload();
  };

  const cellColor = (p, i) => {
    const v = ({ L: 1, M: 2, H: 3 }[p] || 2) * ({ L: 1, M: 2, H: 3 }[i] || 2);
    if (v >= 6) return 'bg-red-500';
    if (v >= 3) return 'bg-amber-500';
    return 'bg-emerald-500';
  };
  const cellRisks = (p, i) => risks.filter(r => r.probability === p && r.impact === i);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div>
        <div className="kpi-overline mb-3">3×3 Risk Heatmap</div>
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <div className="flex">
            <div className="w-8" />
            <div className="flex-1 grid grid-cols-3 gap-1 text-center text-[10px] font-display font-bold uppercase tracking-widest text-slate-500">
              <div>Low</div><div>Med</div><div>High</div>
            </div>
          </div>
          <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-400 text-center mb-1">Impact →</div>
          {['H', 'M', 'L'].map(p => (
            <div key={p} className="flex items-center mb-1">
              <div className="w-8 text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 text-right pr-2">{({ H: 'High', M: 'Med', L: 'Low' })[p]}</div>
              <div className="flex-1 grid grid-cols-3 gap-1">
                {['L', 'M', 'H'].map(i => (
                  <div key={i} className={'aspect-square rounded-sm ' + cellColor(p, i) + ' text-white flex flex-col items-center justify-center text-xs font-bold relative'}>
                    <span className="text-2xl font-display">{cellRisks(p, i).length}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
          <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-400 text-center mt-1">↑ Probability</div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="kpi-overline">Risk Register</div>
          <button data-testid="risk-add-btn" onClick={() => setAdding(true)} className="text-xs flex items-center gap-1 text-[#3b4872] font-display font-semibold"><Plus size={12} /> Add risk</button>
        </div>
        {risks.length === 0 ? (
          <div className="text-center py-12 border border-dashed border-slate-200 rounded-sm text-slate-400 text-xs uppercase tracking-widest font-display">No risks identified yet</div>
        ) : (
          <div className="space-y-2">
            {risks.map(r => (
              <div key={r.id} className="bg-white border border-slate-200 rounded-sm p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="text-sm font-medium leading-snug">{r.description}</div>
                    <div className="text-xs text-slate-500 mt-1">Owner · {r.owner || '—'}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className={'tag ' + (SCORE_COLORS[r.score] || 'bg-slate-100')}>Score · {r.score}</span>
                    <span className="text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">P {r.probability} · I {r.impact}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {adding && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-white rounded-sm p-5 w-full max-w-md grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872]">New</div>
              <h3 className="font-display font-bold text-xl mt-1">Risk Entry</h3>
            </div>
            <div className="col-span-2">
              <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Description</label>
              <textarea value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-[#3b4872] focus:ring-1 focus:ring-[#3b4872] focus:outline-none" />
            </div>
            <SelectInput label="Probability" value={form.probability} onChange={v => setForm({ ...form, probability: v })} options={[{ v: 'L', l: 'Low' }, { v: 'M', l: 'Medium' }, { v: 'H', l: 'High' }]} />
            <SelectInput label="Impact" value={form.impact} onChange={v => setForm({ ...form, impact: v })} options={[{ v: 'L', l: 'Low' }, { v: 'M', l: 'Medium' }, { v: 'H', l: 'High' }]} />
            <div className="col-span-2"><Input label="Owner" value={form.owner} onChange={v => setForm({ ...form, owner: v })} /></div>
            <div className="col-span-2 flex justify-end gap-2 pt-3 border-t border-slate-200">
              <button type="button" onClick={() => setAdding(false)} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-50 font-display">Cancel</button>
              <button type="submit" className="px-4 py-2 text-sm bg-[#3b4872] text-white rounded-sm font-display font-semibold">Add Risk</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────────
// Material Requisitions — Projects team raises MPRs that flow to the
// Procurement module. Backend endpoints:
//   GET  /projects/{id}/mpr   → list MPRs for this project
//   POST /projects/{id}/mpr   → raise a new MPR (notifies Procurement)
// ──────────────────────────────────────────────────────────────────────

const MPR_STATUS_BADGE = {
  Open: 'bg-amber-50 text-amber-700 border border-amber-200',
  'RFQ Issued': 'bg-sky-50 text-sky-700 border border-sky-200',
  Awarded: 'bg-violet-50 text-violet-700 border border-violet-200',
  Closed: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  Rejected: 'bg-rose-50 text-rose-700 border border-rose-200',
};

const MPR_PRIORITY_BADGE = {
  Normal: 'bg-slate-50 text-slate-700 border border-slate-200',
  High: 'bg-amber-50 text-amber-800 border border-amber-200',
  Urgent: 'bg-red-50 text-red-700 border border-red-200',
};

// Roles permitted to raise an MPR. Mirrors the backend's require_write("projects").
// Read-only roles (e.g. "user", "hr") see the list but no Raise button.
const MPR_WRITE_ROLES = new Set(['MD', 'super_admin', 'admin', 'engineer', 'PROJECT_MANAGER']);

function MaterialRequisitionsTab({ project, user }) {
  const [mprs, setMprs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRaise, setShowRaise] = useState(false);
  const canWrite = MPR_WRITE_ROLES.has(user?.role);

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiGet(`/projects/${project.id}/mpr`);
      setMprs(Array.isArray(r?.mprs) ? r.mprs : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load MPRs');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [project?.id]);

  return (
    <div data-testid="project-mpr-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500">Material Requisitions</div>
          <div className="text-sm text-slate-600">
            Raise requests to the Procurement team. {loading ? null : `${mprs.length} on record.`}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="p-2 text-slate-500 hover:text-[#3b4872] hover:bg-slate-50 rounded-sm" title="Refresh" data-testid="mpr-refresh-btn">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
          {canWrite && (
            <button
              onClick={() => setShowRaise(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#3b4872] hover:bg-[#2f3a5d] text-white rounded-sm font-display font-bold uppercase tracking-widest"
              data-testid="mpr-raise-btn"
            >
              <Plus size={13} /> Raise MPR
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-10 text-center text-sm text-slate-500">
          <Loader2 size={18} className="animate-spin mx-auto mb-2" /> Loading material requisitions…
        </div>
      ) : mprs.length === 0 ? (
        <div className="bg-slate-50 border-2 border-dashed border-slate-200 rounded-sm p-10 text-center" data-testid="mpr-empty-state">
          <Package size={32} className="mx-auto text-slate-400 mb-3" />
          <div className="text-sm font-display font-bold text-slate-700">No material requisitions raised yet.</div>
          <div className="text-xs text-slate-500 mt-1">
            {canWrite
              ? <>Click <strong>Raise MPR</strong> to request materials from the Procurement team.</>
              : 'Your project team has not raised any material requisitions yet.'}
          </div>
        </div>
      ) : (
        <div className="border border-slate-200 rounded-sm overflow-hidden">
          <table className="w-full text-sm" data-testid="mpr-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-[10px] font-display font-bold uppercase tracking-widest text-slate-500">
                <th className="text-left px-3 py-2">MPR No</th>
                <th className="text-left px-3 py-2">Item</th>
                <th className="text-right px-3 py-2">Qty + Unit</th>
                <th className="text-left px-3 py-2">Required By</th>
                <th className="text-left px-3 py-2">Priority</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Raised By</th>
              </tr>
            </thead>
            <tbody>
              {mprs.map((m) => (
                <tr key={m.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`mpr-row-${m.id}`}>
                  <td className="px-3 py-2 font-mono text-xs text-slate-700">{m.mpr_no}</td>
                  <td className="px-3 py-2">
                    <div className="font-display font-semibold text-slate-800">{m.item || m.material}</div>
                    {m.description && m.description !== m.item && (
                      <div className="text-[11px] text-slate-500 mt-0.5">{m.description}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs">
                    {Number(m.quantity || m.qty || 0).toLocaleString('en-IN')} {m.unit}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600">
                    {m.required_by ? formatDate(m.required_by) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`tag ${MPR_PRIORITY_BADGE[m.priority] || MPR_PRIORITY_BADGE.Normal}`}>{m.priority || 'Normal'}</span>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`tag ${MPR_STATUS_BADGE[m.status] || MPR_STATUS_BADGE.Open}`}>{m.status || 'Open'}</span>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600">
                    {m.raised_by}
                    {m.raised_by_department && <div className="text-[10px] text-slate-400">{m.raised_by_department}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showRaise && (
        <RaiseMPRModal
          projectId={project.id}
          onClose={() => setShowRaise(false)}
          onSaved={() => { setShowRaise(false); load(); }}
        />
      )}
    </div>
  );
}

const MPR_UNITS = ['Nos', 'kg', 'MT', 'm', 'm²', 'm³', 'litre', 'bag', 'set', 'LS'];

function RaiseMPRModal({ projectId, onClose, onSaved }) {
  const [form, setForm] = useState({
    item: '', description: '', quantity: '', unit: 'Nos',
    required_by: '', priority: 'Normal', estimated_value: '', remarks: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async (e) => {
    e?.preventDefault?.();
    if (!form.item.trim()) return toast.error('Item / Material is required');
    if (!form.quantity || Number(form.quantity) < 1) return toast.error('Quantity must be at least 1');
    setSubmitting(true);
    try {
      await apiPost(`/projects/${projectId}/mpr`, {
        item: form.item.trim(),
        description: form.description.trim() || undefined,
        quantity: Number(form.quantity),
        unit: form.unit,
        required_by: form.required_by || undefined,
        priority: form.priority,
        estimated_value: form.estimated_value ? Number(form.estimated_value) : 0,
        remarks: form.remarks.trim() || undefined,
      });
      toast.success('MPR raised successfully — Procurement team has been notified');
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to raise MPR');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-end md:items-center justify-center p-3" data-testid="mpr-raise-modal">
      <div className="bg-white rounded-sm w-full max-w-2xl max-h-[92vh] overflow-y-auto border border-slate-200">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white">
          <div>
            <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500">Projects → Procurement</div>
            <h3 className="font-display font-bold text-lg">Raise Material Requisition</h3>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-500 hover:text-slate-800" data-testid="mpr-modal-close"><X size={18} /></button>
        </div>

        <form onSubmit={save} className="p-5 grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Item / Material *</label>
            <input
              value={form.item}
              onChange={(e) => set('item', e.target.value)}
              placeholder="e.g. OPC 53 Grade Cement"
              autoFocus
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-[#3b4872] focus:ring-1 focus:ring-[#3b4872] focus:outline-none"
              data-testid="mpr-field-item"
            />
          </div>

          <div className="col-span-2">
            <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              rows={2}
              placeholder="Specifications, brand preferences, grade, IS code…"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-[#3b4872] focus:ring-1 focus:ring-[#3b4872] focus:outline-none"
              data-testid="mpr-field-description"
            />
          </div>

          <div>
            <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Quantity *</label>
            <input
              type="number" min={1} step="0.01"
              value={form.quantity}
              onChange={(e) => set('quantity', e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-[#3b4872] focus:ring-1 focus:ring-[#3b4872] focus:outline-none"
              data-testid="mpr-field-quantity"
            />
          </div>
          <div>
            <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Unit *</label>
            <select
              value={form.unit}
              onChange={(e) => set('unit', e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white focus:border-[#3b4872] focus:ring-1 focus:ring-[#3b4872] focus:outline-none"
              data-testid="mpr-field-unit"
            >
              {MPR_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>

          <div>
            <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Required By</label>
            <input
              type="date"
              value={form.required_by}
              onChange={(e) => set('required_by', e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-[#3b4872] focus:ring-1 focus:ring-[#3b4872] focus:outline-none"
              data-testid="mpr-field-required-by"
            />
          </div>
          <div>
            <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Priority</label>
            <select
              value={form.priority}
              onChange={(e) => set('priority', e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white focus:border-[#3b4872] focus:ring-1 focus:ring-[#3b4872] focus:outline-none"
              data-testid="mpr-field-priority"
            >
              <option>Normal</option>
              <option>High</option>
              <option>Urgent</option>
            </select>
          </div>

          <div className="col-span-2">
            <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Estimated Value (₹)</label>
            <input
              type="number" min={0} step="0.01"
              value={form.estimated_value}
              onChange={(e) => set('estimated_value', e.target.value)}
              placeholder="Optional · helps procurement plan budget approvals"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-[#3b4872] focus:ring-1 focus:ring-[#3b4872] focus:outline-none"
              data-testid="mpr-field-estimated-value"
            />
          </div>

          <div className="col-span-2">
            <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Remarks</label>
            <textarea
              value={form.remarks}
              onChange={(e) => set('remarks', e.target.value)}
              rows={2}
              placeholder="Site-specific notes, drawings reference, etc."
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-[#3b4872] focus:ring-1 focus:ring-[#3b4872] focus:outline-none"
              data-testid="mpr-field-remarks"
            />
          </div>

          <div className="col-span-2 flex items-center justify-end gap-2 pt-3 border-t border-slate-200 mt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-50 font-display">Cancel</button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 text-sm bg-[#3b4872] hover:bg-[#2f3a5d] disabled:opacity-60 text-white rounded-sm font-display font-bold uppercase tracking-widest flex items-center gap-2"
              data-testid="mpr-modal-submit"
            >
              {submitting ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
              {submitting ? 'Raising…' : 'Raise MPR'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
